import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import { useUserContext } from "../_context/UserContext";
import { FaSignOutAlt, FaTimes, FaChevronDown, FaChevronRight } from "react-icons/fa";
import CNALOGO from "../_resources/Images/logo.png";
import "./TopNavigation.css";

interface TopNavigationProps {
  onClose?: () => void;
}

/* ── Tiny SVG icons ─────────────────────────────────────── */
const HomeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);
const UsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const BroomIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 20l-7-7" /><path d="M9 3l-.5 9.5L4 18h16l-4.5-5.5L15 3H9z" />
  </svg>
);
const BuildingIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 9h1m5 0h1M9 13h1m5 0h1M9 17h1m5 0h1" />
  </svg>
);
const ReportIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);
const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const ListIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

/* ── Nav group accordion ─────────────────────────────────── */
interface NavGroupProps {
  icon: React.ReactNode;
  label: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

const NavGroup: React.FC<NavGroupProps> = ({ icon, label, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="sidebar-nav-group">
      <button className="sidebar-nav-group-header" onClick={() => setOpen(o => !o)}>
        <span className="sidebar-nav-group-icon">{icon}</span>
        <span className="sidebar-nav-group-label">{label}</span>
        <span className={`sidebar-nav-group-chevron${open ? " open" : ""}`}>
          <FaChevronDown size={10} />
        </span>
      </button>
      {open && (
        <div className="sidebar-nav-group-items">
          {children}
        </div>
      )}
    </div>
  );
};

/* ── Individual nav link ─────────────────────────────────── */
interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ to, icon, label, onClick }) => (
  <NavLink
    to={to}
    onClick={onClick}
    className={({ isActive }) =>
      `sidebar-nav-link${isActive ? " active" : ""}`
    }
  >
    <span className="sidebar-nav-link-icon">{icon}</span>
    {label}
  </NavLink>
);

/* ── Main component ───────────────────────────────────────── */
export const TopNavigation: React.FC<TopNavigationProps> = ({ onClose }) => {
  const { loggedUser, logout, isUserAdmin } = useUserContext();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleConfirmLogout = () => {
    logout();
    setShowLogoutModal(false);
  };

  const userName: string = loggedUser?.data?.Name ?? "User";
  const initials = userName
    .split(" ")
    .map((w: string) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  // Determine if a route group is active (to auto-open its accordion)
  const isInGroup = (paths: string[]) =>
    paths.some(p => location.pathname.includes(p));

  const handleNavClick = () => onClose?.();

  return (
    <>
      {/* ── Logo ───────────────────────────────────── */}
      <div className="sidebar-logo-area">
        <div className="sidebar-logo-row">
          <img src={CNALOGO} alt="CNA Logo" className="sidebar-logo" />
          {onClose && (
            <button className="sidebar-close-btn" onClick={onClose} aria-label="Close menu">
              <FaTimes />
            </button>
          )}
        </div>
      </div>

      {/* ── User Info (Moved to top) ───────────────── */}
      <div className="sidebar-user-header">
        <div className="sidebar-user-info">
          <div className="sidebar-user-avatar">{initials}</div>
          <div style={{ minWidth: 0 }}>
            <div className="sidebar-user-name">{userName}</div>
            <div className="sidebar-user-role">
              {isUserAdmin ? "Administrator" : "System User"}
            </div>
          </div>
        </div>
      </div>

      {/* ── Navigation ─────────────────────────────── */}
      <nav className="sidebar-nav">

        {/* Dashboard home */}
        <NavItem
          to={isUserAdmin ? "/admin" : "/reports"}
          icon={<HomeIcon />}
          label={isUserAdmin ? "Dashboard" : "Reports Home"}
          onClick={handleNavClick}
        />

        {isUserAdmin ? (
          /* ── Admin nav ── */
          <>
            <div className="sidebar-nav-separator" />
            <span className="sidebar-nav-section-label">Manage</span>

            <NavGroup
              icon={<UsersIcon />}
              label="Users"
              defaultOpen={isInGroup(["/add_system_users", "/view_users"])}
            >
              <NavItem to="/admin/add_system_users" icon={<PlusIcon />} label="Add User" onClick={handleNavClick} />
              <NavItem to="/admin/view_users" icon={<ListIcon />} label="View Users" onClick={handleNavClick} />
            </NavGroup>

            <NavGroup
              icon={<BroomIcon />}
              label="Cleaners"
              defaultOpen={isInGroup(["/add_new_cleaner", "/view_cleaners"])}
            >
              <NavItem to="/admin/add_new_cleaner" icon={<PlusIcon />} label="Add Cleaner" onClick={handleNavClick} />
              <NavItem to="/admin/view_cleaners" icon={<ListIcon />} label="View Cleaners" onClick={handleNavClick} />
            </NavGroup>

            <NavGroup
              icon={<BuildingIcon />}
              label="Clients"
              defaultOpen={isInGroup(["/add_new_client", "/view_clients"])}
            >
              <NavItem to="/admin/add_new_client" icon={<PlusIcon />} label="Add Client" onClick={handleNavClick} />
              <NavItem to="/admin/view_clients" icon={<ListIcon />} label="View Clients" onClick={handleNavClick} />
            </NavGroup>

            <NavGroup
              icon={<ReportIcon />}
              label="Reports"
              defaultOpen={isInGroup(["/view_reports", "/select_client_for_new_report"])}
            >
              <NavItem to="/reports/view_reports" icon={<ListIcon />} label="View Reports" onClick={handleNavClick} />
              <NavItem to="/reports/select_client_for_new_report" icon={<PlusIcon />} label="New Report" onClick={handleNavClick} />
            </NavGroup>
          </>
        ) : (
          /* ── System user (reports) nav ── */
          <>
            <div className="sidebar-nav-separator" />
            <span className="sidebar-nav-section-label">Reports</span>

            <NavGroup
              icon={<ReportIcon />}
              label="Reports"
              defaultOpen={true}
            >
              <NavItem to="/reports/view_reports" icon={<ListIcon />} label="View Reports" onClick={handleNavClick} />
              <NavItem to="/reports/select_client_for_new_report" icon={<PlusIcon />} label="New Report" onClick={handleNavClick} />
            </NavGroup>
          </>
        )}

        {/* ── Logout Button ── */}
        <div className="sidebar-nav-separator" style={{ marginTop: "16px", marginBottom: "16px" }} />
        <button className="sidebar-logout-btn" onClick={() => setShowLogoutModal(true)}>
          <FaSignOutAlt size={14} />
          Sign Out
        </button>
      </nav>

      {/* ── Logout modal ───────────────────────────── */}
      <Modal show={showLogoutModal} onHide={() => setShowLogoutModal(false)} dialogClassName="admin-logout-modal">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Sign Out</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to sign out?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLogoutModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleConfirmLogout}>Sign Out</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};